/**
 * Lets the current session frame consume the Continue button press before the
 * session advances — e.g. a memo deck steps through its cards first, and only
 * the press on the last card moves the session forward.
 */
type Interceptor = () => boolean;

// a stack, not a single slot: two frames can briefly coexist during a screen
// transition, and the older one must get its interceptor back when the newer
// frame unmounts
const stack: Interceptor[] = [];

export const registerNextInterceptor = (interceptor: Interceptor) => {
  stack.push(interceptor);
  return () => {
    const index = stack.indexOf(interceptor);
    if (index !== -1) {
      stack.splice(index, 1);
    }
  };
};

/**
 * true when the press was consumed by one of the registered frames.
 *
 * The whole stack is walked from the innermost registration outwards: a frame
 * and its container both register (React flushes child effects before parent
 * ones, so the container ends up on top), and an interceptor that declines the
 * press must not hide the ones below it.
 */
export const consumeNextPress = (): boolean => {
  for (let index = stack.length - 1; index >= 0; index--) {
    if (stack[index]()) {
      return true;
    }
  }
  return false;
};
